import { ScheduleTicker } from "../../app/schedule-ticker";
import { LocalTransport } from "../../track/local-transport";
import { Observable, type Disposable, type ReadonlyObservable } from "../observable";
import { MetronomeComponent } from "./metronome";

const signatureEquals = (a: Signature, b: Signature): boolean =>
  a.num === b.num && a.den === b.den;

/** Time signature, `num` beats per measure, `den` note value (1, 2, 4, 8, 16). */
export type Signature = {
  readonly num: number;
  readonly den: number;
};

/** Catalog of supported subdivision patterns. */
export type DivisionId =
  | "quarter"
  | "eighths"
  | "triplet"
  | "sixteenth"
  | "dotted"
  | "swing";

/**
 * `hits` is a sorted array of fractional positions inside a single beat,
 * where `0` is the beat downbeat. The first hit (`0`) is the *main* click , 
 * it's the one that drives the pulse / beat events. Subsequent hits are
 * quieter "subdivision" clicks. Values are in [0, 1).
 */
export type Division = {
  readonly id: DivisionId;
  readonly label: string;
  readonly hint: string;
  readonly hits: readonly number[];
};

export const DIVISIONS: readonly Division[] = [
  { id: "quarter", label: "Noires", hint: "1 clic / temps", hits: [0] },
  { id: "eighths", label: "Croches", hint: "2 clics / temps", hits: [0, 0.5] },
  { id: "triplet", label: "Triolet", hint: "3 clics / temps", hits: [0, 1 / 3, 2 / 3] },
  { id: "sixteenth", label: "Doubles croches", hint: "4 clics / temps", hits: [0, 0.25, 0.5, 0.75] },
  { id: "dotted", label: "Croche pointée", hint: "Croche pointée + double", hits: [0, 0.75] },
  { id: "swing", label: "Swing", hint: "Long-court", hits: [0, 2 / 3] },
] as const;

export const DEFAULT_SIGNATURE: Signature = { num: 4, den: 4 };
export const DEFAULT_DIVISION: DivisionId = "quarter";

export type ContinuousMetronomeBeatEvent = {
  /** 0..(beatsPerMeasure - 1) */
  beat: number;
  /** Total measures elapsed since start (0 on the first beat). */
  measure: number;
  /** True when this beat is the downbeat of a measure AND accent is enabled. */
  isAccent: boolean;
  /** AudioContext.currentTime at which this beat is scheduled. */
  audioTime: number;
  /**
   * `performance.now()` value corresponding to when the audio click will
   * actually play. Listeners fire up to lookahead (~100ms) before the click,
   * so consumers needing precise timing should use this rather than calling
   * `performance.now()` themselves.
   */
  realTimeMs: number;
};

export type ContinuousMetronomeListener = (
  event: ContinuousMetronomeBeatEvent,
) => void;

/**
 * Continuous metronome scheduler.
 *
 * Reuses MetronomeComponent for the audio click and ScheduleTicker's lookahead
 * scheduler for sample-accurate beat timing, the same precision pattern
 * as the count-in. Designed to be reusable beyond drill tempo (other
 * future modes like rhythm exercises, tempo training, etc.).
 *
 * Supports time signatures and beat subdivisions. The signature `den` is
 * not yet used to resolve beat duration (the metronome assumes the beat is
 * the displayed beat), but is preserved on the type so listeners can
 * react. Beat events fire at the *main* click of each beat; subdivision
 * clicks are scheduled silently in audio.
 */
export class ContinuousMetronome implements Disposable {
  private _active = false;
  private _bpm = 90;
  /** Resolved division (cached from `_divisionId` for the scheduling hot path). */
  private _resolvedDivision: Division = DIVISIONS[0];

  private _signature = new Observable<Signature>(DEFAULT_SIGNATURE, {
    equals: signatureEquals,
    onChange: (v) => this.applySignature(v),
  });

  private _divisionId = new Observable<DivisionId>(DEFAULT_DIVISION, {
    onChange: (id) => this.applyDivision(id),
  });

  private _accent = new Observable<boolean>(true);

  // Free-mode (legacy) scheduling state, used by drills/journey.
  private nextBeatTime = 0;
  private nextBeat = 0;
  private nextMeasure = 0;

  // Synced-mode scheduling state, used when driven by a LocalTransport.
  private syncedTransport: LocalTransport | null = null;
  private unsubscribeSchedule: (() => void) | null = null;
  private unsubscribeEvents: (() => void) | null = null;
  private lastScheduledBeatTick = -1;
  // Tick captured on the last play/seek event. Used on the first scheduling
  // round after a play/seek so the downbeat click isn't skipped by Math.ceil
  // when fromTick has already advanced past it. -1 = no pending play/seek.
  private resumeFromTick = -1;

  private muted = false;

  private listeners = new Set<ContinuousMetronomeListener>();
  private boundOnSchedule: (audioTime: number, scheduleUntil: number) => void;

  constructor(
    private readonly metronome: MetronomeComponent,
    private readonly ticker: ScheduleTicker,
  ) {
    this.boundOnSchedule = (audioTime, scheduleUntil) =>
      this.onSchedule(audioTime, scheduleUntil);
  }

  /**
   * Start the scheduler. Two call shapes are supported:
   *  - `start(bpm, beatsPerMeasure?)`, legacy, used by drills/journey.
   *    `beatsPerMeasure` overrides the current signature numerator.
   *  - signature/division come from the current state set via setters.
   */
  start(bpm: number, beatsPerMeasure?: number): void {
    if (this._active) {
      return;
    }

    this._bpm = bpm;

    if (typeof beatsPerMeasure === "number" && beatsPerMeasure > 0) {
      const current = this._signature.value;
      this._signature.set({ num: beatsPerMeasure, den: current.den });
    }

    this.nextBeat = 0;
    this.nextMeasure = 0;
    // Small delay so the first click doesn't fire in the past.
    this.nextBeatTime = this.ticker.audioTime + 0.1;
    this._active = true;

    this.ticker.register(this.boundOnSchedule);
  }

  /**
   * Start the scheduler synced to a LocalTransport. Clicks are scheduled at
   * tick boundaries derived from the transport's musical timeline, so the
   * audio click and the piano-roll playhead share the *same* time source.
   *
   * While the transport is paused, no clicks fire. Seek/play resume cleanly
   * (the next downbeat/beat is recomputed from the current playhead tick).
   *
   * Tempo and signature counting:
   *  - The duration of one beat comes from `timeline.getTicksPerBeat` →
   *    follows tempo changes / playback rate / song time signature.
   *  - The "beat-in-bar" index used for the accent and pulse-dots event
   *    follows `this._signature.num`. When a song is loaded, callers should
   *    push the song's signature to the metronome so visuals match the
   *    piano-roll bars.
   */
  startSynced(localTransport: LocalTransport): void {
    if (this._active) {
      return;
    }

    this._active = true;
    this.syncedTransport = localTransport;
    this.lastScheduledBeatTick = -1;
    this.resumeFromTick = -1;

    this.unsubscribeSchedule = localTransport.register(
      (fromTick, toTick) => this.onTransportSchedule(fromTick, toTick),
    );

    this.unsubscribeEvents = localTransport.on((event) => {
      if (event.type === "seek" || event.type === "play") {
        this.lastScheduledBeatTick = -1;
        this.resumeFromTick = event.tick;
      }
    });
  }

  stop(): void {
    if (!this._active) {
      return;
    }

    this._active = false;

    if (this.syncedTransport !== null) {
      this.unsubscribeSchedule?.();
      this.unsubscribeEvents?.();
      this.unsubscribeSchedule = null;
      this.unsubscribeEvents = null;
      this.syncedTransport = null;
      this.lastScheduledBeatTick = -1;
      this.resumeFromTick = -1;

      return;
    }

    this.ticker.unregister(this.boundOnSchedule);
  }

  setBpm(bpm: number): void {
    this._bpm = bpm;
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
  }

  /** Update signature on the fly. The change applies from the next beat. */
  setSignature(signature: Signature): void {
    this._signature.set(signature);
  }

  /** Update the subdivision pattern on the fly. */
  setDivision(id: DivisionId): void {
    this._divisionId.set(id);
  }

  setAccent(accent: boolean): void {
    this._accent.set(accent);
  }

  get bpm(): number {
    return this._bpm;
  }

  get signature(): ReadonlyObservable<Signature> {
    return this._signature;
  }

  get division(): ReadonlyObservable<DivisionId> {
    return this._divisionId;
  }

  get accent(): ReadonlyObservable<boolean> {
    return this._accent;
  }

  get isRunning(): boolean {
    return this._active;
  }

  /** Subscribe to beat events. Returns an unsubscribe function. */
  onBeat(listener: ContinuousMetronomeListener): () => void {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  }

  dispose(): void {
    this.stop();
    this.listeners.clear();
    this._signature.dispose();
    this._divisionId.dispose();
    this._accent.dispose();
  }

  private applySignature(signature: Signature): void {
    // Resync beat counter so we don't desync on a numerator shrink mid-bar.
    if (this.nextBeat >= signature.num) {
      this.nextBeat = 0;
    }
  }

  private applyDivision(id: DivisionId): void {
    this._resolvedDivision = DIVISIONS.find((d) => d.id === id) ?? DIVISIONS[0];
  }

  private onSchedule(audioTime: number, scheduleUntil: number): void {
    // Rattrapage après un arrêt du fil — onglet en arrière-plan, `setTimeout`
    // étranglé — pendant lequel l'horloge audio a continué d'avancer : sans
    // ça, tous les temps écoulés partaient d'un coup à des dates passées, en
    // salve de clics. On saute au prochain temps à venir, compteurs compris.
    if (this._active && this.nextBeatTime < audioTime) {
      const beatDuration = 60 / this._bpm;
      const missed = Math.ceil((audioTime - this.nextBeatTime) / beatDuration);
      const num = this._signature.value.num;
      const total = this.nextBeat + missed;

      this.nextBeatTime += missed * beatDuration;
      this.nextMeasure += Math.floor(total / num);
      this.nextBeat = total % num;
    }

    while (this._active && this.nextBeatTime <= scheduleUntil) {
      const isDownbeat = this.nextBeat === 0;
      const isAccent = isDownbeat && this._accent.value;
      const beatDuration = 60 / this._bpm;

      if (!this.muted) {
        // Main click for this beat.
        this.metronome.scheduleAt(
          this.nextBeatTime,
          isAccent ? "accent" : "normal",
        );

        // Subdivision clicks within the beat (skip index 0, that's the main).
        const hits = this._resolvedDivision.hits;

        for (let i = 1; i < hits.length; i++) {
          const offset = hits[i] * beatDuration;
          this.metronome.scheduleAt(this.nextBeatTime + offset, "subdivision");
        }
      }

      // Compute the real-time (performance.now) at which the audio click
      // will actually play, by offsetting from "now" by the audio-clock
      // delta until the scheduled click.
      const offsetSec = this.nextBeatTime - audioTime;
      const realTimeMs = performance.now() + offsetSec * 1000;

      const event: ContinuousMetronomeBeatEvent = {
        beat: this.nextBeat,
        measure: this.nextMeasure,
        isAccent,
        audioTime: this.nextBeatTime,
        realTimeMs,
      };
      this.listeners.forEach((cb) => cb(event));
      this.metronome.emitBeat(this.nextBeat, isAccent);

      // Advance
      this.nextBeat = (this.nextBeat + 1) % this._signature.value.num;

      if (this.nextBeat === 0) {
        this.nextMeasure++;
      }

      this.nextBeatTime += beatDuration;
    }
  }

  private onTransportSchedule(fromTick: number, toTick: number): void {
    const lt = this.syncedTransport;

    if (lt === null) {
      return;
    }

    const timeline = lt.transport.timeline;
    const safeFromTick = Math.max(0, fromTick);
    const ticksPerBeat = timeline.getTicksPerBeat(safeFromTick);

    if (ticksPerBeat <= 0) {
      return;
    }

    let beatTick: number;

    if (this.resumeFromTick >= 0) {
      // First scheduling round after a play/seek: anchor on the resume tick
      // (not on `fromTick`, which has already advanced by ~25 ms because the
      // scheduler runs after `play()`, that would skip the downbeat click
      // when resuming exactly on a beat).
      const resumeTick = this.resumeFromTick;

      beatTick = resumeTick % ticksPerBeat === 0
        ? resumeTick
        : Math.ceil(resumeTick / ticksPerBeat) * ticksPerBeat;

      this.resumeFromTick = -1;
    } else {
      beatTick = Math.ceil(safeFromTick / ticksPerBeat) * ticksPerBeat;

      if (
        this.lastScheduledBeatTick >= 0 &&
        beatTick <= this.lastScheduledBeatTick
      ) {
        beatTick = this.lastScheduledBeatTick + ticksPerBeat;
      }
    }

    while (beatTick <= toTick) {
      const tpb = timeline.getTicksPerBeat(beatTick);

      if (tpb <= 0) {
        break;
      }

      const ticksPerBar = tpb * this._signature.value.num;
      const tickInBar = ((beatTick % ticksPerBar) + ticksPerBar) % ticksPerBar;
      const beatInBar = Math.round(tickInBar / tpb);
      const measure = Math.floor(beatTick / ticksPerBar);

      const isDownbeat = beatInBar === 0;
      const isAccent = isDownbeat && this._accent.value;
      const audioTime = lt.getTimeFromTick(beatTick);

      if (!this.muted) {
        this.metronome.scheduleAt(
          audioTime,
          isAccent ? "accent" : "normal",
        );

        const hits = this._resolvedDivision.hits;

        for (let i = 1; i < hits.length; i++) {
          const subTick = beatTick + hits[i] * tpb;
          this.metronome.scheduleAt(
            lt.getTimeFromTick(subTick),
            "subdivision",
          );
        }
      }

      const offsetSec = audioTime - this.ticker.audioTime;
      const realTimeMs = performance.now() + offsetSec * 1000;

      const event: ContinuousMetronomeBeatEvent = {
        beat: beatInBar,
        measure,
        isAccent,
        audioTime,
        realTimeMs,
      };
      this.listeners.forEach((cb) => cb(event));
      this.metronome.emitBeat(beatInBar, isAccent);

      this.lastScheduledBeatTick = beatTick;
      beatTick += tpb;
    }
  }
}
